import {Component, forwardRef, ViewChild, EventEmitter, Output} from '@angular/core';
import {ENTER} from '@angular/cdk/keycodes';
import {MatChipInputEvent} from '@angular/material/chips';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {RatingsFilterDialogComponent} from "../ratings-filter-dialog/ratings-filter-dialog.component";
import {WidgetFilter} from "../../core/models";
import {MatMenuTrigger} from "@angular/material/menu";
import {interval} from "rxjs/internal/observable/interval";
import {of} from "rxjs/internal/observable/of";
import {delayWhen, takeUntil, tap} from "rxjs/operators";
import {Subscription} from "rxjs/Subscription";
import {fromEvent} from "rxjs/internal/observable/fromEvent";
import {FilterCountryDialogComponent} from "../filter-country-dialog/filter-country-dialog.component";
import {FilterParamDialogComponent} from "../filter-param-dialog/filter-param-dialog.component";
import {FilterSimilarDialogComponent} from "../filter-similar-dialog/filter-similar-dialog.component";
import {FilterUniversityDialogComponent} from "../filter-university-dialog/filter-university-dialog.component";
import {element} from "protractor";
import {CoreState, FiltersCheckResult} from "../../core/state/core.state";
import {Store} from "@ngxs/store";
import {CheckFilters, LoadVisualizations} from "../../core/state/core.actions";
import {Subject} from "rxjs/index";
import {CheckFiltersDialogComponent} from "../check-filters-dialog/check-filters-dialog.component";

const noop = (value?: any) => {
};

interface Disabled {
  block: boolean,
  operation: boolean,
  not: boolean,
  openBracket: boolean,
  closeBracket: boolean,
}

@Component({
  selector: 'query-filter',
  templateUrl: './query-filter.component.html',
  styleUrls: ['./query-filter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => QueryFilterComponent),
      multi: true
    }
  ]
})
export class QueryFilterComponent implements ControlValueAccessor {
  destroyed$ = new Subject();

  @Output() filtersChanged: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild(MatMenuTrigger)
  contextMenu: MatMenuTrigger;
  contextMenuPosition = { x: '0px', y: '0px' };
  windowClickSubscription: Subscription;
  menuSubscription: Subscription;

  disabled: Disabled = {
    block: false,
    operation: true,
    not: true,
    openBracket: false,
    closeBracket: true,
  };

  readonly opMenu = {
    'and': {v: 'AND', label: 'AND', t: 'op'},
    'or': {v: 'OR', label: 'OR', t: 'op'},
    '!': {v: '!', name: '!', t: 'op'},
    'in': {v: 'in', name: 'IN', t: 'op'}
  };

  readonly separatorKeysCodes: number[] = [ENTER];
  private _onChange = noop;
  private _onTouched = noop;

  public syntaxCorrect: boolean = false;
  public logicCorrect: boolean = false;
  public checkResult: FiltersCheckResult = {total: 0, text: '', checked: false};
  public checkText: string = '';

  filterFields = [];

    constructor(private store: Store,
                private dialog: MatDialog) {
    this.filtersChanged.subscribe(event => {
      this.updateDisabled();
      this.check();
    });

  }

  ngOnInit(): void {
    this.windowClickSubscription = fromEvent(window, 'click').subscribe((_) => {
      if (this.contextMenu.menuOpen) {
        this.contextMenu.closeMenu();
      }
    });

    this.store.select((s) => s.core.checkFiltersResult).pipe(
      takeUntil(this.destroyed$)
    ).subscribe(
      (v: FiltersCheckResult) => {
        if(v) {
          this.checkResult = v;
          this.logicCorrect = this.filterFields.length == 0 || v.total > 0;
          if(!this.logicCorrect) {
            this.checkText = `Ошибка логики: ${v.text}`;
          }
        }
    });
  }

  ngOnDestroy(): void {
    this.windowClickSubscription && this.windowClickSubscription.unsubscribe();
    this.menuSubscription && this.menuSubscription.unsubscribe();
    this.destroyed$.next();
    this.destroyed$.complete();

  }

  writeValue(obj: any) {
    if (obj) {
      this.filterFields = [...obj];
      this.updateDisabled();
      this.check();
    }
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
  }

  add(event: MatChipInputEvent): void {
    const input = event.input;
    const value = (event.value || '').trim();

    const token = this.opMenu[value.toLowerCase()];

    if (token) {
      this.filterFields.push(token);
    } else if (value) {
      this.filterFields.push({name: value.trim(), v: value.trim(), t: 'val'});
    }

    if (input) {
      input.value = '';
    }
    this.refresh();
  }

  removeBlock(f: any): void {
    const index = this.filterFields.indexOf(f);

    if (index >= 0) {
      this.filterFields.splice(index, 1);
      let isOpPrev = false;
      let isOp;

      for(let i=0 ; i<this.filterFields.length ; i++) {
        isOp = this.filterFields[i].t == 'op' && ['OR', 'AND'].includes(this.filterFields[i].v);
        if((isOpPrev && isOp) || (i==0 && isOp)) {
          this.filterFields.splice(i, 1);
          i--;
        }
        isOpPrev = isOp;
      }

      this.refresh();
      this.filtersChanged.emit('remove');
    }
  }

  addFilterOp(v) {
    this.filterFields.push({...v, t: 'op'});
    this.refresh();
    this.filtersChanged.emit('newop');
  }

  refresh() {
    this._onChange([...this.filterFields]);
    this._onTouched();
  }

  addFilterBlock(type: string) {
    let dialogRef;
    const config = {
      width: '500px',
      autoFocus: false,
      data: {}
    };
    dialogRef = this.getDialogRef(type, config);

    dialogRef.afterClosed().subscribe(
      (val) => {
        if (val) {
          this.filterFields.push(val.data);
          this.refresh();
          this.filtersChanged.emit('new');
        }
      }
    );
  }

  onContextMenu(event: MouseEvent, item) {
    event.preventDefault();
    let remove = true;
    let change = true;
    if(item.t == 'op' && item.v == 'NOT') {
      change = false;
    } else {
      if (item.t == 'op' && this.filterFields.indexOf(item) < this.filterFields.length - 1) {
        remove = false;
      }
      if (item.t == 'op' && (item.v == '(' || item.v == ')')) {
        change = false;
      }
    }
    this.contextMenu.menuData = { 'item': item, 'remove': remove, 'change': change };

    this.menuSubscription && this.menuSubscription.unsubscribe();
    this.menuSubscription = of(1)
      .pipe(
        tap(() => {
          if (this.contextMenu.menuOpen) {
            this.contextMenu.closeMenu();
          }
          this.contextMenuPosition.x = event.clientX + 'px';
          this.contextMenuPosition.y = event.clientY + 'px';
        }),
        delayWhen((_) => (this.contextMenu.menuOpen ? interval(200) : of(undefined))),
        tap(async() => {
          this.contextMenu.openMenu();
          let backdrop: HTMLElement = null;
          do {
            await this.delay(100);
            backdrop = document.querySelector(
              'div.cdk-overlay-backdrop.cdk-overlay-transparent-backdrop.cdk-overlay-backdrop-showing'
            ) as HTMLElement;
          } while (backdrop === null);
          backdrop.style.pointerEvents = 'none';
        })
      )
      .subscribe();
  }

  delay(delayInms: number) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(2);
      }, delayInms);
    });
  }

  editBlock(f) {
    let dialogRef;
    const index = this.filterFields.indexOf(f);
    if(f.t == 'op') {
      // оператор
      if(f.v == 'OR') {
        this.filterFields[index] = {label: "AND", t: "op", v: "AND"};
      } else {
        this.filterFields[index] = {label: "OR", t: "op", v: "OR"};
      }
      this.check();
      this.refresh();
      return;
    }
    const type = f.type === undefined ? f.var : f.type;

    const filter = new WidgetFilter(
      type,
      f.op,
      f.val,
      f.university,
      f.indicators,
      f.method,
      f.indicatorsFilter,
      f.count,
    );
    dialogRef = this.getDialogRef(type, {width: '500px', autoFocus: false, data: filter});

    dialogRef.afterClosed().subscribe(
      (val) => {
        if (val) {
          this.filterFields[index] = val.data;
          this.refresh();
          this.filtersChanged.emit('edit');
        }
      }
    );
  }

  getDialogRef(type: string, config) {
    if(type.startsWith('country')) {
      return this.dialog.open(FilterCountryDialogComponent, config);
    } else if(type == 'name') {
      config.width = '1100px';
      return this.dialog.open(FilterUniversityDialogComponent, config);
    } else if(type == 'reference') {
      config.width = '726px';
      return this.dialog.open(FilterSimilarDialogComponent, config);
    } else if(type == 'ratings') {
      config.width = '726px';
        return this.dialog.open(RatingsFilterDialogComponent, config);
    }
    return this.dialog.open(FilterParamDialogComponent, config);

  }

  updateDisabled() {
    if(this.filterFields.length == 0) {
      this.disabled = {
        block: false,
        operation: true,
        not: false,
        openBracket: false,
        closeBracket: true,
      };
      return;
    }
    const lastEl = this.filterFields[this.filterFields.length-1];
    const isOp = lastEl.t == 'op' && ['OR', 'AND'].includes(lastEl.v);
    const isBlock = lastEl.op != undefined || lastEl.type != undefined;
    const isNot = lastEl.t == 'op' && lastEl.v == 'NOT';
    const isOpenBracket = lastEl.t == 'op' && lastEl.v == '(';
    const isCloseBracket = lastEl.t == 'op' && lastEl.v == ')';

    const openBrackets = this.filterFields.filter(v => v.t == 'op' && v.v == '(' ).length;
    const closeBrackets = this.filterFields.filter(v => v.t == 'op' && v.v == ')' ).length;
    const availableCloseBracket = openBrackets > closeBrackets;

    this.disabled.block = ! (isOp || isOpenBracket || isNot);
    this.disabled.operation = ! (isBlock || isCloseBracket) || isNot;
    this.disabled.not = ! (isOp || isOpenBracket) || isNot;
    this.disabled.openBracket = ! (isOp || isNot);
    this.disabled.closeBracket = ! (isBlock && availableCloseBracket);
  }

  openCheckDialog() {
    this.dialog.open(CheckFiltersDialogComponent, {data: {
        syntaxCorrect: this.syntaxCorrect,
        logicCorrect: this.logicCorrect,
        checkResult: this.checkResult,
    }});
  }

  check() {
    let bracketsCorrect = this.checkBrackets();
    let operationsCorrect = false;
    if (bracketsCorrect) {
      operationsCorrect = this.checkOperations();

    }

    this.syntaxCorrect = bracketsCorrect && operationsCorrect;
    if (!bracketsCorrect) {
      this.checkText = 'Ошибка синтаксиса: не закрыты скобки';
    } else if (!operationsCorrect) {
      this.checkText = 'Ошибка синтаксиса: пропущен или некорректно указан логический оператор';
    }

    if(this.syntaxCorrect) {
      this.checkText = '';
      this.checkUnions();
    } else {

    }
  }

  checkBrackets() : boolean {
    const brackets = this.filterFields.filter(f => f.t == 'op' && ['(', ')'].includes(f.v)).map(f => f.v);
    let sum = 0;
    for (let i=0 ; i<brackets.length ; i++) {
      sum += brackets[i] == '(' ? 1 : -1;
      if (sum < 0) {
        return false;
      }
    }
    return sum == 0;
  }

  checkOperations() : boolean {
    let el = '';
    let prev = '';
    for (let i=0 ; i<this.filterFields.length ; i++) {
      // const f = this.filterFields[i];
      el = this.filterFields[i].t == 'op' ? this.filterFields[i].v : 'block';

      if (i == 0) {
        // первым можно только NOT, ( или блок
        if ( ! ['NOT', '(', 'block'].includes(el)) {
          console.log(i, prev, el, 'первым можно только NOT, ( или блок');
          return false;
        }
      } else {

        // две подряд операции нельзя за исключением, когда после операции идет NOT или (
        if (['AND', 'OR',].includes(el) && ['AND', 'OR',].includes(prev)) {
          console.log(i, prev, el, 'две подряд операции нельзя за исключением, когда после операции идет NOT или (');
          return false;
        }

        // два подряд NOT нельзя
        if (el == 'NOT' && prev == 'NOT') {
          console.log(i, prev, el, 'два подряд NOT нельзя');
          return false;
        }

        // два подряд блока нельзя
        if (el == 'block' && prev == 'block') {
          console.log(i, prev, el, 'два подряд блока нельзя');
          return false;
        }

        // должен быть блок перед закрывающей скобкой
        if (el == ')' && prev != 'block') {
          console.log(i, prev, el, 'должен быть блок перед закрывающей скобкой');
          return false;
        }

        // после открывающей скобки можно только NOT или блок
        if (prev == '(' && ! ['NOT', 'block'].includes(el) ) {
          console.log(i, prev, el, ' после открывающей скобки можно только NOT или блок');
          return false;
        }

      }

      prev = el;
    }
    return true;
  }

  checkUnions() {
    if(this.filterFields.length > 0) {
      this.store.dispatch(
        new CheckFilters(this.filterFields)
      );
    } else {
      this.logicCorrect = true;
    }
  }

}
